import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * DurationDialog — quick presets + custom for duration & unit + optional qty.
 * Value = { duration: number, duration_unit: 'day'|'week'|'month'|'year', qty: number }
 */
const UNITS = [
  { key: "day", label: "Day(s)" },
  { key: "week", label: "Week(s)" },
  { key: "month", label: "Month(s)" },
  { key: "year", label: "Year(s)" },
];

export default function DurationDialog({ open, onOpenChange, value, onChange }) {
  const v = value || { duration: 3, duration_unit: "day", qty: 0 };
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState(v.duration || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[24px] max-w-md" data-testid="duration-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Duration & Qty</DialogTitle>
          <DialogDescription className="sr-only">Set duration with a preset or custom value, and quantity.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <div>
            <div className="text-sm font-semibold mb-2 text-[#1F2A24]">Duration</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <button
                  key={n} type="button"
                  onClick={() => { setCustom(false); onChange({ ...v, duration: n }); }}
                  data-testid={`duration-preset-${n}`}
                  className={`w-10 h-10 rounded-lg text-sm font-semibold border ${!custom && v.duration === n ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}
                >{n}</button>
              ))}
              <div className="w-3" />
              {UNITS.map((u) => (
                <button
                  key={u.key} type="button"
                  onClick={() => onChange({ ...v, duration_unit: u.key })}
                  data-testid={`duration-unit-${u.key}`}
                  className={`px-3 h-10 rounded-lg text-sm font-semibold border ${v.duration_unit === u.key ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}
                >{u.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCustom(true)}
                data-testid="duration-custom"
                className={`h-10 px-4 rounded-lg text-sm font-semibold border ${custom ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}
              >Custom</button>
              <div className="inline-flex items-center bg-white border border-[#E8E6E1] rounded-lg">
                <button type="button" className="w-8 h-10 text-lg" onClick={() => setCustomVal(Math.max(0, Number(customVal) - 1))}>−</button>
                <input type="number" min={0} className="w-14 text-center bg-transparent outline-none" value={customVal} onChange={(e) => setCustomVal(e.target.value)} />
                <button type="button" className="w-8 h-10 text-lg" onClick={() => setCustomVal(Number(customVal) + 1)}>+</button>
              </div>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-2 text-[#1F2A24]">Qty</div>
            <div className="inline-flex items-center bg-white border border-[#E8E6E1] rounded-lg pl-3">
              <input type="number" min={0} className="w-16 py-2 text-lg bg-transparent outline-none" value={v.qty ?? 0} onChange={(e) => onChange({ ...v, qty: Number(e.target.value) })} data-testid="duration-qty" />
              <span className="px-3 text-xs text-[#8A9992]">nos</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-outline-emerald" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            className="btn-emerald"
            onClick={() => {
              const finalDur = custom ? Number(customVal) : v.duration;
              onChange({ ...v, duration: finalDur });
              onOpenChange(false);
            }}
            data-testid="duration-save"
          >Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
